import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import QuickForm from '../components/forms/QuickForm';
import { coursesMock } from '../data/mock';
import { terminyZS, terminyZSByMonth } from '../data/terminy';
import { 
  GraduationCap, Clock, Users, CheckCircle2,
  ShieldAlert, AlertTriangle, FileText, MessageSquare,
  Brain, Scale, Eye, Phone, Calendar
} from 'lucide-react';

const course = coursesMock.zs;

export default function ZSPage() {
  const [selectedTermin, setSelectedTermin] = useState('');

  const handleReservation = (termin) => {
    const terminValue = `#${termin.id} - ${termin.lekce1} a ${termin.lekce2} (${termin.cas} - ${termin.typ})`;
    setSelectedTermin(terminValue);
    
    // Scroll to form
    setTimeout(() => {
      const form = document.getElementById('reservation-form');
      form?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-cyan-50/30" />
          <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-blue-100/40 rounded-full blur-3xl" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex flex-wrap gap-3 mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full text-blue-700 text-sm font-medium">
                  <GraduationCap className="w-4 h-4" />
                  <span>Kurz pro základní školy</span>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-100 rounded-full text-emerald-700 text-sm font-medium">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Financovatelné z OP JAK II</span>
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight mb-4">
                {course.title}
              </h1>
              
              <p className="text-xl md:text-2xl text-blue-700 font-medium mb-4">
                {course.subtitle}
              </p>

              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                {course.tagline}
              </p>

              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-2 text-slate-700">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <span>{course.duration}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <Users className="w-5 h-5 text-blue-600" />
                  <span>Prezenčně min. {course.minParticipantsPresence} / Online bez omezení</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <span className="text-2xl font-bold text-slate-900">{course.price.toLocaleString('cs-CZ')} Kč</span>
                  <span className="text-slate-500">/ účastník</span>
                </div>
              </div>
            </div>

            {/* Quick Form in Hero */}
            <div className="mt-12 max-w-xl">
              <div className="bg-white rounded-2xl p-6 shadow-xl shadow-blue-100/50 border border-blue-100">
                <div className="flex items-center gap-2 mb-4">
                  <Phone className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-slate-900">Nechte nám kontakt, ozveme se</span>
                </div>
                <QuickForm courseType="zs" buttonText="Chci se dozvědět víc" variant="hero" />
              </div>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                  Umělá inteligence už není téma budoucnosti
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  Zásadně ovlivňuje každodenní práci pedagogů – přípravu výuky, hodnocení, 
                  komunikaci s rodiči i oblast školní prevence.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed mb-8">
                  Kurz AI pro pedagogy pomáhá učitelům:
                </p>
                
                <div className="space-y-4">
                  {course.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <span className="text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="bg-gradient-to-br from-blue-100 via-cyan-50 to-teal-100 rounded-2xl p-12 shadow-2xl">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-white/80 backdrop-blur rounded-xl p-6 shadow-lg">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4 mx-auto">
                        <Brain className="w-6 h-6 text-blue-600" />
                      </div>
                      <p className="text-center text-slate-700 font-medium">Příprava výuky</p>
                    </div>
                    <div className="bg-white/80 backdrop-blur rounded-xl p-6 shadow-lg">
                      <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center mb-4 mx-auto">
                        <Scale className="w-6 h-6 text-cyan-600" />
                      </div>
                      <p className="text-center text-slate-700 font-medium">Hodnocení</p>
                    </div>
                    <div className="bg-white/80 backdrop-blur rounded-xl p-6 shadow-lg">
                      <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center mb-4 mx-auto">
                        <MessageSquare className="w-6 h-6 text-teal-600" />
                      </div>
                      <p className="text-center text-slate-700 font-medium">Komunikace</p>
                    </div>
                    <div className="bg-white/80 backdrop-blur rounded-xl p-6 shadow-lg">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4 mx-auto">
                        <ShieldAlert className="w-6 h-6 text-blue-600" />
                      </div>
                      <p className="text-center text-slate-700 font-medium">Prevence</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Challenges */}
        <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Co dnes pedagogové skutečně řeší
              </h2>
              <p className="text-lg text-slate-600">
                Pedagogové dnes stojí před velmi praktickými otázkami:
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {course.challenges.map((challenge, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md hover:border-blue-100 transition-all">
                  <Brain className="w-6 h-6 text-blue-500 mb-4" />
                  <p className="text-slate-700 leading-relaxed">{challenge}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Risks */}
        <section className="py-20 bg-gradient-to-br from-rose-50 to-orange-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-100 rounded-full text-rose-700 text-sm font-medium mb-6">
                  <ShieldAlert className="w-4 h-4" />
                  <span>Prevence a bezpečnost</span>
                </div>
                
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                  Prevence a rizika AI ve škole
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-8">
                  AI přináší i nová rizika, se kterými se školy reálně setkávají. 
                  Tyto situace vznikají rychle, anonymně a obtížně se řeší tradičními postupy.
                </p>

                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {course.risks.map((risk, index) => (
                    <div key={index} className="flex items-center gap-3 bg-white/80 rounded-lg p-4">
                      <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-slate-700">{risk}</span>
                    </div>
                  ))}
                </div>

                <p className="text-slate-800 font-medium flex items-center gap-2">
                  <Eye className="w-5 h-5 text-rose-600" />
                  Bez porozumění AI dnes nelze dělat smysluplnou prevenci.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <h3 className="text-xl font-bold text-slate-900 mb-6">
                  Pro metodiky prevence
                </h3>
                <p className="text-slate-600 mb-6">
                  Kurz poskytuje metodikům prevence:
                </p>
                <div className="space-y-4">
                  {course.methodikBenefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Admin benefits */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                  Méně administrativy,<br />více času na skutečnou pedagogickou práci
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  Součástí kurzu je také práce s AI jako administrativní oporou pedagoga. 
                  Nejde o didaktiku ani o obsah výuky, ale o činnosti, které učitelům 
                  dlouhodobě berou čas a energii.
                </p>
                <p className="text-slate-600 mb-6">V kurzu ukazujeme, jak využít AI ke:</p>
                <div className="space-y-4">
                  {course.adminBenefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      <span className="text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-8 text-slate-500">
                  Cílem je omezit zbytečné papírování a rutinní psaní, aby pedagogům 
                  zůstalo více času na výuku, práci s třídou a individuální podporu žáků.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-blue-100">
                  <FileText className="w-8 h-8 text-blue-500 mb-4" />
                  <p className="text-slate-900 font-medium">Formální podklady</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg border border-blue-100">
                  <Scale className="w-8 h-8 text-cyan-500 mb-4" />
                  <p className="text-slate-900 font-medium">Zadání a hodnocení</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg border border-blue-100">
                  <MessageSquare className="w-8 h-8 text-teal-500 mb-4" />
                  <p className="text-slate-900 font-medium">Komunikace s rodiči</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg border border-blue-100">
                  <ShieldAlert className="w-8 h-8 text-blue-500 mb-4" />
                  <p className="text-slate-900 font-medium">Preventivní hodiny</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Jak kurz probíhá
              </h2>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 md:p-12 border border-blue-100">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-7 h-7 text-blue-600" />
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Rozsah</p>
                  <p className="font-semibold text-slate-900 text-sm">{course.duration}</p>
                </div>
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <GraduationCap className="w-7 h-7 text-blue-600" />
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Forma</p>
                  <p className="font-semibold text-slate-900">{course.format}</p>
                </div>
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-7 h-7 text-blue-600" />
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Min. účastníků</p>
                  <p className="font-semibold text-slate-900 text-sm">Prezenčně {course.minParticipantsPresence} / Online bez omezení</p>
                </div>
                <div className="text-center">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Financování</p>
                  <p className="font-semibold text-slate-900">OP JAK II / DVPP</p>
                </div>
              </div>

              <div className="text-center bg-white rounded-xl p-8 shadow-sm">
                <p className="text-slate-600 mb-2">Cena kurzu</p>
                <p className="text-4xl font-bold text-slate-900 mb-2">
                  {course.price.toLocaleString('cs-CZ')} Kč
                  <span className="text-lg font-normal text-slate-500"> / účastník</span>
                </p>
                <p className="text-sm text-slate-500 mb-4">
                  Platba probíhá standardně fakturou na vaši školu.
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                    Financovatelné z OP JAK II
                  </span>
                  <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium">
                    Financovatelné z DVPP
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why us */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-12">
              Proč právě tento kurz
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <div className="text-4xl font-bold text-blue-600 mb-2">1 500+</div>
                <p className="text-slate-600">proškolených pedagogů</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-600 mb-2">100%</div>
                <p className="text-slate-600">z reálné školní praxe</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-600 mb-2">8h</div>
                <p className="text-slate-600">praktické práce</p>
              </div>
            </div>
            <p className="text-lg text-slate-600 mt-10">
              Výstupy jsou okamžitě použitelné ve škole.
            </p>
          </div>
        </section>

        {/* Termíny školení */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Dostupné termíny 2026
              </h2>
              <p className="text-lg text-slate-600">
                Vyberte si termín a rezervujte místo
              </p>
            </div>

            {/* Termíny po měsících */}
            <div className="space-y-12">
              {Object.entries(terminyZSByMonth).map(([month, terminy]) => (
                <div key={month}>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">
                    {month}
                  </h3>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-b">#</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-b">Lekce 1</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-b">Lekce 2</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-b">Čas</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-b">Typ</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-b"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {terminy.map((termin, index) => (
                          <tr key={termin.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                            <td className="px-4 py-3 text-sm font-bold text-blue-600 border-b">#{termin.id}</td>
                            <td className="px-4 py-3 text-sm text-slate-900 border-b">{termin.lekce1}</td>
                            <td className="px-4 py-3 text-sm text-slate-900 border-b">{termin.lekce2}</td>
                            <td className="px-4 py-3 text-sm text-slate-700 border-b">{termin.cas}</td>
                            <td className="px-4 py-3 border-b">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${
                                termin.typ === 'ranní' 
                                  ? 'bg-amber-100 text-amber-700' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {termin.typ}
                              </span>
                            </td>
                            <td className="px-4 py-3 border-b">
                              <button
                                onClick={() => handleReservation(termin)}
                                className="bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                              >
                                Rezervovat
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Big CTA Section */}
        <section id="reservation-form" className="py-20 bg-gradient-to-br from-blue-600 to-cyan-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Máte zájem o kurz pro vaši školu?
              </h2>
              <p className="text-lg text-blue-100">
                Nechte nám kontakt a my se vám ozveme s termíny a informacemi k OP JAK II.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-2xl">
              <QuickForm courseType="zs" buttonText="Chci se dozvědět víc" variant="hero" preselectedTermin={selectedTermin} />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
